/**
 * Team Leaderboards — free group competition.
 *
 * Teams can be created by anyone (free feature).
 * Teams compete on: average strength score, total volume, workout frequency.
 * Team leaderboard is visible to all — drives coach/club upgrades.
 *
 * Revenue path: Free creates teams → Coach manages them → Club owns the gym.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('TEAM_LEADERBOARD');

export interface TeamRanking {
  team_id: string;
  team_name: string;
  captain_id: string;
  member_count: number;
  avg_strength: number;
  total_volume: number;
  active_members: number; // members who logged a workout in last 14 days
  rank: number;
}

export interface TeamMemberStrength {
  profile_id: string;
  handle: string | null;
  display_name: string | null;
  strength_score: number;
  last_workout_at: string | null;
  role: 'captain' | 'co_captain' | 'member';
}

/**
 * Fetch team leaderboard — ranked by average strength of top 10 members.
 * Same top-10 averaging as gym battle to prevent beginner exclusion.
 */
export async function fetchTeamLeaderboard(): Promise<TeamRanking[]> {
  try {
    const { data: teams, error: teamsErr } = await supabase
      .from('teams')
      .select('id, name, captain_id')
      .eq('is_active', true);

    if (teamsErr) {
      log.error('fetchTeamLeaderboard teams query failed:', teamsErr.message);
      return [];
    }

    if (!teams || teams.length === 0) return [];

    const rankings: TeamRanking[] = [];
    const fourteenDaysAgo = new Date(Date.now() - 14 * 86_400_000).toISOString();

    for (const team of teams) {
      const { data: members } = await supabase
        .from('team_members')
        .select('profile_id, role, profiles(duel_standard_strength_kg, last_workout_at)')
        .eq('team_id', team.id);

      if (!members || members.length === 0) continue;

      const scores = members
        .map((m: any) => (m.profiles?.duel_standard_strength_kg as number) ?? 0)
        .filter((s: number) => s > 0)
        .sort((a: number, b: number) => b - a);

      // Top-10 average (same as gym battle — prevents beginner exclusion)
      const top10 = scores.slice(0, 10);
      const avg =
        top10.length > 0
          ? top10.reduce((s: number, v: number) => s + v, 0) / top10.length
          : 0;

      const activeMembers = members.filter(
        (m: any) =>
          m.profiles?.last_workout_at &&
          m.profiles.last_workout_at > fourteenDaysAgo,
      ).length;

      rankings.push({
        team_id: team.id,
        team_name: team.name,
        captain_id: team.captain_id,
        member_count: members.length,
        avg_strength: Math.round(avg),
        total_volume: 0, // computed separately if needed
        active_members: activeMembers,
        rank: 0,
      });
    }

    // Sort by avg_strength descending and assign ranks
    rankings.sort((a, b) => b.avg_strength - a.avg_strength);
    rankings.forEach((r, i) => {
      r.rank = i + 1;
    });

    return rankings;
  } catch (err) {
    log.error('fetchTeamLeaderboard failed:', err);
    return [];
  }
}

/**
 * Get team members with their strength scores.
 */
export async function getTeamMemberStrengths(
  teamId: string,
): Promise<TeamMemberStrength[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select(
      'profile_id, role, profiles(handle, display_name, duel_standard_strength_kg, last_workout_at)',
    )
    .eq('team_id', teamId);

  if (error) {
    log.error('getTeamMemberStrengths failed:', error.message);
    return [];
  }

  return (data ?? []).map((m: any) => ({
    profile_id: m.profile_id,
    handle: m.profiles?.handle ?? null,
    display_name: m.profiles?.display_name ?? null,
    strength_score: m.profiles?.duel_standard_strength_kg ?? 0,
    last_workout_at: m.profiles?.last_workout_at ?? null,
    role: m.role,
  }));
}

/**
 * Generate a shareable team invite link.
 */
export function generateTeamInviteLink(teamId: string): string {
  return `https://douevenlift.com/team/${teamId}?utm_source=duel&utm_medium=share&utm_campaign=team_invite`;
}

/**
 * Generate a public read-only leaderboard URL for a team.
 * Accessible without an account — for teachers, parents, spectators.
 */
export function generateTeamPublicUrl(teamId: string): string {
  return `https://douevenlift.com/team/${teamId}/leaderboard?utm_source=duel&utm_medium=share&utm_campaign=team_view`;
}

/**
 * Generate a public leaderboard URL for a group of teams.
 * For coaches managing multiple teams (e.g. position groups on a football team).
 */
export function generateGroupLeaderboardUrl(teamIds: string[]): string {
  const ids = teamIds.join(',');
  return `https://douevenlift.com/group?teams=${encodeURIComponent(ids)}&utm_source=duel&utm_medium=share&utm_campaign=group_view`;
}

/**
 * Get all teams a profile belongs to.
 */
export async function getMyTeams(
  profileId: string,
): Promise<{ team_id: string; team_name: string; role: string }[]> {
  const { data, error } = await supabase
    .from('team_members')
    .select('team_id, role, teams(name)')
    .eq('profile_id', profileId);

  if (error) {
    log.error('getMyTeams failed:', error.message);
    return [];
  }

  return (data ?? []).map((m: any) => ({
    team_id: m.team_id,
    team_name: m.teams?.name ?? 'Unknown Team',
    role: m.role,
  }));
}

/**
 * Issue a Team vs Team challenge.
 */
export async function challengeTeam(
  challengerTeamId: string,
  challengedTeamId: string,
  title: string,
  durationDays: number,
  createdBy: string,
): Promise<boolean> {
  try {
    const { error } = await supabase.from('team_challenges').insert({
      challenger_team_id: challengerTeamId,
      challenged_team_id: challengedTeamId,
      title,
      status: 'pending',
      start_date: new Date().toISOString(),
      end_date: new Date(Date.now() + durationDays * 86_400_000).toISOString(),
      created_by: createdBy,
    });

    if (error) {
      log.error('challengeTeam failed:', error.message);
      return false;
    }
    return true;
  } catch (err) {
    log.error('challengeTeam exception:', err);
    return false;
  }
}
